/**
 * Case- and accent-insensitive text matching shared by search boxes, pickers
 * and highlighters, so filtering and highlighting always agree.
 */

export const HIGHLIGHT_CLASS = 'bg-yellow-200 text-inherit rounded-sm px-0.5'

function foldChars(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
}

/** Lowercases, strips accents and collapses whitespace. */
export function normalizeForSearch(text: string): string {
  return foldChars(text).replace(/\s+/g, ' ').trim()
}

/**
 * Finds every occurrence of `query` in `text`, ignoring case and accents.
 * Returns [start, end) ranges in indexes of the original text.
 */
export function findMatchRanges(text: string, query: string): Array<[number, number]> {
  const needle = normalizeForSearch(query)
  if (!needle) return []

  let haystack = foldChars(text)
  // Folding keeps a 1:1 index mapping for Latin text. Only build an explicit
  // map when some character expanded or shrank (e.g. ligatures).
  let originIndex: number[] | null = null
  if (haystack.length !== text.length) {
    haystack = ''
    originIndex = []
    for (let i = 0; i < text.length; i++) {
      const folded = foldChars(text[i])
      for (let j = 0; j < folded.length; j++) originIndex.push(i)
      haystack += folded
    }
    originIndex.push(text.length)
  }

  const ranges: Array<[number, number]> = []
  let from = 0
  while (from <= haystack.length - needle.length) {
    const index = haystack.indexOf(needle, from)
    if (index === -1) break
    const end = index + needle.length
    ranges.push(originIndex ? [originIndex[index], originIndex[end]] : [index, end])
    from = end
  }
  return ranges
}

/**
 * Wraps every match of `query` inside the text nodes of an already sanitized
 * HTML string with <mark>. Tags and attributes are never touched. Must run
 * AFTER sanitization: the output is trusted only because the input was.
 */
export function highlightHtml(sanitizedHtml: string, query: string): string {
  if (!sanitizedHtml || !normalizeForSearch(query)) return sanitizedHtml

  const root = document.createElement('div')
  root.innerHTML = sanitizedHtml

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  let node: Node | null
  while ((node = walker.nextNode())) textNodes.push(node as Text)

  for (const textNode of textNodes) {
    const text = textNode.data
    const ranges = findMatchRanges(text, query)
    if (ranges.length === 0) continue

    const fragment = document.createDocumentFragment()
    let cursor = 0
    for (const [start, end] of ranges) {
      if (start > cursor) fragment.appendChild(document.createTextNode(text.slice(cursor, start)))
      const mark = document.createElement('mark')
      mark.className = HIGHLIGHT_CLASS
      mark.textContent = text.slice(start, end)
      fragment.appendChild(mark)
      cursor = end
    }
    if (cursor < text.length) fragment.appendChild(document.createTextNode(text.slice(cursor)))

    textNode.replaceWith(fragment)
  }

  return root.innerHTML
}
